import { useState, useEffect, useRef } from 'react';
import { X, Loader, Search } from 'lucide-react';
import { createActivity, getContacts } from '../services/api';

interface QuickLogModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type ActivityType = 'call' | 'meeting' | 'note' | 'task';

const activityTypes: { key: ActivityType; label: string }[] = [
  { key: 'call', label: 'Call' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'note', label: 'Note' },
  { key: 'task', label: 'Task' },
];

interface ContactOption {
  id: string;
  name: string;
}

export default function QuickLogModal({ open, onClose, onSuccess }: QuickLogModalProps) {
  const [type, setType] = useState<ActivityType>('note');
  const [contactSearch, setContactSearch] = useState('');
  const [contactId, setContactId] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (open) {
      setType('note');
      setContactSearch('');
      setContactId('');
      setContactName('');
      setContactOptions([]);
      setDescription('');
      setDueDate('');
      setError('');
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Contact autocomplete
  useEffect(() => {
    if (!contactSearch.trim() || contactSearch === contactName) {
      setContactOptions([]);
      setShowDropdown(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await getContacts({ search: contactSearch, limit: '8' });
        const items = data?.items ?? data ?? [];
        const options = Array.isArray(items)
          ? items.map((c: any) => ({
              id: c.id,
              name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unknown',
            }))
          : [];
        setContactOptions(options);
        setShowDropdown(options.length > 0);
      } catch {
        setContactOptions([]);
      }
    }, 300);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [contactSearch, contactName]);

  if (!open) return null;

  const selectContact = (option: ContactOption) => {
    setContactId(option.id);
    setContactName(option.name);
    setContactSearch(option.name);
    setShowDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        type,
        subject: `${type.charAt(0).toUpperCase() + type.slice(1)}: ${description.substring(0, 60)}`,
        description: description.trim(),
      };
      if (contactId) payload.contact_id = contactId;
      if (type === 'task' && dueDate) payload.due_date = dueDate;

      await createActivity(payload);
      onClose();
      onSuccess?.();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to log activity');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-primary)',
    color: 'var(--text-primary)',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.75)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full md:w-[480px] md:max-h-[90vh] max-h-[85vh] overflow-y-auto rounded-t-2xl md:rounded-2xl p-6 slide-up"
        style={{ background: '#141414', border: '1px solid var(--border-primary)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-lg" style={{ color: 'var(--text-primary)' }}>
            Log Activity
          </h3>
          <button
            onClick={onClose}
            className="min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg cursor-pointer"
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Drag handle on mobile */}
        <div className="md:hidden flex justify-center -mt-4 mb-4">
          <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border-subtle)' }} />
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--error)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type selector pills */}
          <div>
            <label className="block text-xs mb-1.5 font-body" style={{ color: 'var(--text-secondary)' }}>
              Type
            </label>
            <div className="flex gap-2 flex-wrap">
              {activityTypes.map((at) => (
                <button
                  key={at.key}
                  type="button"
                  onClick={() => setType(at.key)}
                  className="min-h-[48px] px-4 rounded-full text-sm font-body cursor-pointer transition-all"
                  style={{
                    background: type === at.key ? 'var(--netrun-green)' : 'var(--bg-input)',
                    color: type === at.key ? 'var(--netrun-black)' : 'var(--text-secondary)',
                    border: `1px solid ${type === at.key ? 'var(--netrun-green)' : 'var(--border-subtle)'}`,
                  }}
                >
                  {at.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact search */}
          <div className="relative">
            <label className="block text-xs mb-1.5 font-body" style={{ color: 'var(--text-secondary)' }}>
              Contact
            </label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => {
                  setContactSearch(e.target.value);
                  if (contactId && e.target.value !== contactName) {
                    setContactId('');
                    setContactName('');
                  }
                }}
                placeholder="Search contacts..."
                className="w-full pl-10 pr-4 min-h-[48px] rounded-lg text-sm outline-none transition-all duration-200"
                style={inputStyle}
                onFocus={(e) => e.currentTarget.style.borderColor = 'var(--netrun-green)'}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-primary)';
                  // Delay hiding dropdown so click registers
                  setTimeout(() => setShowDropdown(false), 200);
                }}
              />
            </div>
            {showDropdown && (
              <div
                className="absolute left-0 right-0 top-full mt-1 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)' }}
              >
                {contactOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => selectContact(opt)}
                    className="w-full text-left px-4 min-h-[48px] text-sm cursor-pointer flex items-center transition-colors"
                    style={{ color: 'var(--text-primary)', background: 'transparent', border: 'none' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {opt.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs mb-1.5 font-body" style={{ color: 'var(--text-secondary)' }}>
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened? What was discussed?"
              rows={3}
              className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 resize-none"
              style={inputStyle}
              onFocus={(e) => e.currentTarget.style.borderColor = 'var(--netrun-green)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-primary)'}
            />
          </div>

          {/* Due date for tasks */}
          {type === 'task' && (
            <div>
              <label className="block text-xs mb-1.5 font-body" style={{ color: 'var(--text-secondary)' }}>
                Due Date
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-4 min-h-[48px] rounded-lg text-sm outline-none transition-all duration-200"
                style={inputStyle}
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-[48px] rounded-lg text-sm font-body cursor-pointer transition-all"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 min-h-[48px] rounded-lg text-sm font-body cursor-pointer transition-all flex items-center justify-center gap-2"
              style={{
                background: 'var(--netrun-green)',
                border: 'none',
                color: 'var(--netrun-black)',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting && <Loader size={16} className="animate-spin" />}
              {submitting ? 'Saving...' : 'Log Activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
